import { type Draft, produce } from 'immer';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { useLazyRef } from 'utils/hooks/useLazyRef';

// biome-ignore lint/suspicious/noConfusingVoidType: Fully intentional there, does not work without it
type ValidRecipeReturnType<TState> = TState | void | undefined;
export type UpdateRecipe<TState, D = Draft<TState>> = (
  draft: D,
) => ValidRecipeReturnType<D>;
export type DraftStateUpdater<TState> = (recipe: UpdateRecipe<TState>) => void;
export type StateProxy<TState> = [() => TState, DraftStateUpdater<TState>];

export function useStateProxy<TState>(
  initialState: TState | (() => TState),
): StateProxy<TState>;
export function useStateProxy<TState = undefined>(): StateProxy<
  TState | undefined
>;
export function useStateProxy<TState = undefined>(
  initialState?: TState | (() => TState),
): StateProxy<TState | undefined> {
  const [, forceUpdate] = useReducer((n) => n + 1, 0);
  const stateRef = useLazyRef<TState | undefined>(initialState);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return useMemo(() => {
    let updateRunning = false;
    return [
      () => stateRef.current,
      (recipe) => {
        if (updateRunning) {
          throw new Error(
            'Nested StateProxy.DraftStateUpdater calls are not allowed, the outer update would override the inner one',
          );
        }
        const prevState = stateRef.current;
        try {
          updateRunning = true;
          stateRef.current = (produce as any)(prevState, recipe);
        } finally {
          updateRunning = false;
        }
        if (mounted.current && prevState !== stateRef.current) {
          forceUpdate();
        }
      },
    ];
  }, []);
}
